import {
  UploadIndicationRequest,
  UploadIndicationResponse,
} from '@/modules/leadIndication/entities/leadIndication.interface';
import {
  MovesValidationError,
  movesValidationErrorInitialState,
} from '@/services/files/endpoints/moves';
import { FilesService } from '@/shared/services';
import { get } from 'lodash';
import { useState } from 'react';

const filesService = new FilesService();

export interface UseUploadIndicationOptions {
  onSuccess?: (response: UploadIndicationResponse, entity: UploadIndicationRequest) => void;
  onValidationError?: (errors: MovesValidationError) => void;
}

/**
 * Prepara e executa o upload do arquivo de indicações para a API de Files,
 * assim como no módulo de Posição Cadastral.
 *
 * Monta o `FormData` (arquivo + metadados) e envia via `FilesService`.
 *
 * Não existe endpoint de validação separado: quando o arquivo é inválido,
 * os erros de validação vêm na própria resposta de erro do upload (em
 * `error.response.data`), seguindo o mesmo contrato usado em Posição
 * Cadastral e Movimentação (`MovesValidationError`). Esses erros são
 * expostos em `errors` e via callback `onValidationError`.
 */
const useUploadIndication = ({ onSuccess, onValidationError }: UseUploadIndicationOptions = {}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<MovesValidationError>(movesValidationErrorInitialState);

  const buildFormData = ({
    file,
    referringCompanyId,
    referringCompanyName,
    referringCompanyCnpj,
  }: UploadIndicationRequest): FormData => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('referringCompanyId', referringCompanyId);
    formData.append('referringCompanyName', referringCompanyName);
    formData.append('referringCompanyCnpj', referringCompanyCnpj);

    return formData;
  };

  const uploadIndication = async (entity: UploadIndicationRequest) => {
    setLoading(true);
    setErrors(movesValidationErrorInitialState);

    try {
      const formData = buildFormData(entity);
      const response = await filesService.sendLeadIndication<UploadIndicationResponse>(formData);
      onSuccess?.(response, entity);
      return response;
    } catch (error) {
      console.error(error);

      const errorResponse = get(
        error,
        'response.data',
        movesValidationErrorInitialState,
      ) as MovesValidationError;

      if (errorResponse.details?.length) {
        setErrors(errorResponse);
        onValidationError?.(errorResponse);
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    uploadIndication,
    loading,
    errors,
  };
};

export default useUploadIndication;
